import { toPersianDigits } from '@saay/formatting';
import {
  type ChangeEvent,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import {
  createNumberStepperState,
  reduceNumberStepper,
  type NumberStepperConfig,
} from './headless/stepper-machine.js';
import { MOBILE_MASK, type MaskSpec, useMaskedInput } from './headless/mask-input.js';
import { FieldSupportSlot, revealTruncatedLabel } from './field-support.js';
import type { IconName } from './icon-paths.js';
import { Icon } from './icons.js';
import { IconButton } from './primitives.js';

export type TextFieldVariant = 'outlined' | 'filled';

interface FieldSupportProps {
  readonly counter?: string | undefined;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id: string;
}

function FieldSupport({ counter, error, hint, id }: FieldSupportProps) {
  const hintId = hint === undefined || hint === null ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const counterId = counter === undefined ? undefined : `${id}-counter`;
  return (
    <FieldSupportSlot
      className="saay-textfield__support"
      error={error}
      errorClassName="saay-textfield__error"
      errorId={errorId}
      hint={hint}
      hintClassName="saay-textfield__hint"
      hintId={hintId}
      trailing={
        counter === undefined ? undefined : (
          <span className="saay-textfield__counter" id={counterId}>
            {counter}
          </span>
        )
      }
    />
  );
}

function useFieldIds(id?: string) {
  const generated = useId();
  const fieldId = id ?? generated;
  return fieldId;
}

function describedByIds(
  fieldId: string,
  options: { counter?: string; error?: string; hint?: ReactNode },
): string | undefined {
  const hintId =
    options.hint === undefined || options.hint === null ? undefined : `${fieldId}-hint`;
  const errorId = options.error === undefined ? undefined : `${fieldId}-error`;
  const counterId = options.counter === undefined ? undefined : `${fieldId}-counter`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  return [errorId ?? hintId, counterId].filter(Boolean).join(' ') || undefined;
}

function counterLabel(length: number, maxLength: number): string | undefined {
  if (maxLength <= 0) {
    return undefined;
  }
  const threshold = Math.ceil(maxLength * 0.8);
  if (length < threshold) {
    return undefined;
  }
  return `${toPersianDigits(String(length))}/${toPersianDigits(String(maxLength))}`;
}

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'prefix'
> {
  readonly clearable?: boolean;
  /** Server `fieldErrors[].messageFa` or a client validation message. */
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly leadingIcon?: IconName;
  /** Live mask; submitted value is {@link MaskSpec} raw digits. */
  readonly mask?: MaskSpec;
  readonly maskRaw?: string;
  readonly onMaskRawChange?: (raw: string) => void;
  readonly optional?: boolean;
  readonly prefix?: ReactNode;
  readonly readOnly?: boolean;
  readonly suffix?: ReactNode;
  readonly trailingValid?: boolean;
  readonly variant?: TextFieldVariant;
  /** Left-to-right content inside the RTL form (mobile numbers, codes, Sheba). */
  readonly ltr?: boolean;
}

type TextFieldBodyProps = TextFieldProps & { readonly fieldId: string };

const TextFieldBody = forwardRef<HTMLInputElement, TextFieldBodyProps>(
  function TextFieldBody(props, ref) {
    const {
      clearable = false,
      disabled,
      error,
      fieldId,
      hint,
      label,
      leadingIcon,
      mask,
      maskRaw,
      maxLength,
      onChange,
      onMaskRawChange,
      optional = false,
      prefix,
      readOnly,
      suffix,
      trailingValid = false,
      value,
      variant = 'outlined',
      ltr = false,
      ...rest
    } = props;
    const invalid = error !== undefined;
    const [focused, setFocused] = useState(false);
    const mergedRef = useRef<HTMLInputElement | null>(null);
    const isMasked = mask !== undefined;

    const masked = useMaskedInput({
      onRawChange: onMaskRawChange ?? (() => undefined),
      raw: maskRaw ?? '',
      spec: mask ?? MOBILE_MASK,
    });

    const displayValue = isMasked
      ? masked.display
      : value === undefined
        ? undefined
        : String(value);

    const lengthForCounter = isMasked ? (maskRaw ?? '').length : String(value ?? '').length;
    const counter = maxLength === undefined ? undefined : counterLabel(lengthForCounter, maxLength);

    const describedBy = describedByIds(fieldId, {
      ...(counter === undefined ? {} : { counter }),
      ...(error === undefined ? {} : { error }),
      ...(hint === undefined ? {} : { hint }),
    });
    const showClear =
      clearable &&
      focused &&
      !readOnly &&
      disabled !== true &&
      (isMasked ? (maskRaw ?? '') !== '' : String(value ?? '') !== '');

    function setRefs(node: HTMLInputElement | null) {
      mergedRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref !== null) {
        ref.current = node;
      }
      if (isMasked) {
        masked.inputRef.current = node;
      }
    }

    function handleClear() {
      const input = mergedRef.current;
      if (input === null) {
        return;
      }
      if (isMasked) {
        onMaskRawChange?.('');
      } else if (onChange !== undefined) {
        onChange({
          currentTarget: input,
          target: { name: input.name, value: '' },
          type: 'change',
        } as ChangeEvent<HTMLInputElement>);
      } else {
        const nativeSet = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
        nativeSet?.call(input, '');
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      input.focus();
    }

    const labelText =
      optional && typeof label === 'string' ? (
        <>
          {label} <span className="saay-textfield__optional">(اختیاری)</span>
        </>
      ) : (
        label
      );

    return (
      <div
        className="saay-textfield"
        data-disabled={disabled === true || undefined}
        data-invalid={invalid || undefined}
        data-readonly={readOnly === true || undefined}
        data-variant={variant}
      >
        <label
          className="saay-textfield__label"
          htmlFor={fieldId}
          onPointerEnter={revealTruncatedLabel}
        >
          {labelText}
        </label>
        <div className="saay-textfield__control">
          {prefix === undefined ? null : (
            <span className="saay-textfield__affix saay-textfield__affix--prefix">{prefix}</span>
          )}
          {leadingIcon === undefined ? null : (
            <Icon className="saay-textfield__leading-icon" name={leadingIcon} />
          )}
          <input
            {...rest}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-readonly={readOnly || undefined}
            dir={ltr ? 'ltr' : (rest.dir ?? 'auto')}
            disabled={disabled}
            id={fieldId}
            maxLength={isMasked ? undefined : maxLength}
            onBlur={(event) => {
              setFocused(false);
              rest.onBlur?.(event);
            }}
            onChange={(event) => {
              if (isMasked) {
                masked.onChange(event);
                return;
              }
              onChange?.(event);
            }}
            onFocus={(event) => {
              setFocused(true);
              rest.onFocus?.(event);
            }}
            readOnly={readOnly}
            ref={setRefs}
            value={displayValue}
          />
          {suffix === undefined ? null : (
            <span className="saay-textfield__affix saay-textfield__affix--suffix">{suffix}</span>
          )}
          {trailingValid ? (
            <Icon className="saay-textfield__trailing-icon" name="check-circle" />
          ) : null}
          {showClear ? (
            <IconButton
              className="saay-textfield__clear"
              icon="close"
              label="پاک کردن"
              onClick={handleClear}
              onMouseDown={(event) => event.preventDefault()}
              type="button"
              variant="standard"
            />
          ) : null}
        </div>
        <FieldSupport counter={counter} error={error} hint={hint} id={fieldId} />
      </div>
    );
  },
);

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { id, ...rest },
  ref,
) {
  const fieldId = useFieldIds(id);
  return (
    <TextFieldBody {...rest} fieldId={fieldId} {...(id === undefined ? {} : { id })} ref={ref} />
  );
});

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly optional?: boolean;
  readonly variant?: TextFieldVariant;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    error,
    hint,
    id,
    label,
    maxLength,
    onChange,
    optional = false,
    rows,
    value,
    variant = 'outlined',
    ...rest
  },
  ref,
) {
  const fieldId = useFieldIds(id);
  const invalid = error !== undefined;
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const minRows = rows ?? 3;
  const maxRows = 8;

  useEffect(() => {
    const node = textRef.current;
    if (node === null) {
      return;
    }
    node.style.height = 'auto';
    const lineHeight = Number.parseFloat(getComputedStyle(node).lineHeight) || 20;
    const maxHeight = lineHeight * maxRows;
    node.style.height = `${Math.min(node.scrollHeight, maxHeight)}px`;
  }, [value, maxRows]);

  const length = String(value ?? '').length;
  const counter = maxLength === undefined ? undefined : counterLabel(length, maxLength);
  const describedBy = describedByIds(fieldId, {
    ...(counter === undefined ? {} : { counter }),
    ...(error === undefined ? {} : { error }),
    ...(hint === undefined ? {} : { hint }),
  });

  const labelText =
    optional && typeof label === 'string' ? (
      <>
        {label} <span className="saay-textfield__optional">(اختیاری)</span>
      </>
    ) : (
      label
    );

  function setRefs(node: HTMLTextAreaElement | null) {
    textRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref !== null) {
      ref.current = node;
    }
  }

  return (
    <div
      className="saay-textfield saay-textfield--textarea"
      data-invalid={invalid || undefined}
      data-variant={variant}
    >
      <label
        className="saay-textfield__label"
        htmlFor={fieldId}
        onPointerEnter={revealTruncatedLabel}
      >
        {labelText}
      </label>
      <div className="saay-textfield__control">
        <textarea
          {...rest}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className="saay-textfield__textarea"
          dir="auto"
          id={fieldId}
          onChange={onChange}
          ref={setRefs}
          rows={minRows}
          value={value}
        />
      </div>
      <FieldSupport counter={counter} error={error} hint={hint} id={fieldId} />
    </div>
  );
});

export interface NumberFieldProps {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly max: number;
  readonly min: number;
  readonly name?: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: number | null) => void;
  readonly optional?: boolean;
  readonly step?: number;
  readonly suffix?: ReactNode;
  readonly value: number | null;
  readonly variant?: TextFieldVariant;
  /** Supporting label when the value is exactly zero (I8, e.g. «همیشه»). */
  readonly zeroLabel?: string;
}

/** Bounded numeric field with −/+ steppers (`role="spinbutton"`, material-standard §3.3, I8). */
export function NumberField({
  disabled = false,
  error,
  hint,
  id,
  label,
  max,
  min,
  name,
  onBlur,
  onChange,
  optional = false,
  step = 1,
  suffix,
  value,
  variant = 'outlined',
  zeroLabel,
}: NumberFieldProps) {
  const fieldId = useFieldIds(id);
  const invalid = error !== undefined;
  const config: NumberStepperConfig = { max, min, step };
  const [state, setState] = useState(() => createNumberStepperState(value));

  useEffect(() => {
    setState(createNumberStepperState(value));
  }, [value]);

  function commit(next: ReturnType<typeof reduceNumberStepper>) {
    setState(next);
    onChange(next.value);
  }

  const zeroHint = value === 0 && zeroLabel !== undefined ? zeroLabel : undefined;
  const supportHint = zeroHint ?? hint;
  const describedBy = describedByIds(fieldId, {
    ...(error === undefined ? {} : { error }),
    ...(supportHint === undefined ? {} : { hint: supportHint }),
  });
  const labelText =
    optional && typeof label === 'string' ? (
      <>
        {label} <span className="saay-textfield__optional">(اختیاری)</span>
      </>
    ) : (
      label
    );

  return (
    <div
      className="saay-textfield saay-textfield--number"
      data-disabled={disabled || undefined}
      data-invalid={invalid || undefined}
      data-variant={variant}
    >
      <label
        className="saay-textfield__label"
        htmlFor={fieldId}
        onPointerEnter={revealTruncatedLabel}
      >
        {labelText}
      </label>
      <div className="saay-textfield__control saay-textfield__control--number">
        <IconButton
          aria-label="کاهش"
          disabled={disabled || (state.value ?? min) <= min}
          icon="minus"
          label="کاهش"
          onClick={() => commit(reduceNumberStepper(state, { type: 'decrement' }, config))}
          type="button"
          variant="outlined"
        />
        <input
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={state.value ?? undefined}
          autoComplete="off"
          dir="ltr"
          disabled={disabled}
          id={fieldId}
          inputMode="numeric"
          name={name}
          onBlur={() => {
            commit(reduceNumberStepper(state, { type: 'blur' }, config));
            onBlur?.();
          }}
          onChange={(event) =>
            commit(reduceNumberStepper(state, { text: event.target.value, type: 'input' }, config))
          }
          onKeyDown={(event) => {
            if (disabled) {
              return;
            }
            switch (event.key) {
              case 'ArrowUp':
                event.preventDefault();
                commit(reduceNumberStepper(state, { type: 'increment' }, config));
                break;
              case 'ArrowDown':
                event.preventDefault();
                commit(reduceNumberStepper(state, { type: 'decrement' }, config));
                break;
              case 'PageUp':
                event.preventDefault();
                commit(reduceNumberStepper(state, { type: 'page-increment' }, config));
                break;
              case 'PageDown':
                event.preventDefault();
                commit(reduceNumberStepper(state, { type: 'page-decrement' }, config));
                break;
              case 'Home':
                event.preventDefault();
                commit(reduceNumberStepper(state, { type: 'home' }, config));
                break;
              case 'End':
                event.preventDefault();
                commit(reduceNumberStepper(state, { type: 'end' }, config));
                break;
              default:
                break;
            }
          }}
          role="spinbutton"
          type="text"
          // A human count shows Persian digits like every other number (the parser accepts any).
          value={toPersianDigits(state.text)}
        />
        <IconButton
          aria-label="افزایش"
          disabled={disabled || (state.value ?? min) >= max}
          icon="plus"
          label="افزایش"
          onClick={() => commit(reduceNumberStepper(state, { type: 'increment' }, config))}
          type="button"
          variant="outlined"
        />
        {suffix === undefined ? null : (
          <span className="saay-textfield__affix saay-textfield__affix--suffix">{suffix}</span>
        )}
      </div>
      <FieldSupport error={error} hint={supportHint} id={fieldId} />
    </div>
  );
}
